/**
 * Bidirectional swipe-to-mark, ported from the prototype.
 *
 * Thresholds are the prototype's settled values, not re-tuned here: commit past
 * 33% of the row's width, or on a flick faster than 0.11 px/ms in either
 * direction. Vertical intent wins early so the list still scrolls normally.
 */
import { useRef, useState } from "react";
import type { PointerEvent as ReactPointerEvent } from "react";

const COMMIT_FRACTION = 0.33;
const FLICK_VELOCITY = 0.11;
/** Below this the gesture is a tap; above it we claim the pointer. */
const SLOP_PX = 8;

export interface SwipeState {
  offset: number;
  armed: boolean;
  direction: "left" | "right" | null;
  dragging: boolean;
}

const IDLE: SwipeState = { offset: 0, armed: false, direction: null, dragging: false };

export function useSwipe(onCommit: (direction: "left" | "right") => void) {
  const [state, setState] = useState<SwipeState>(IDLE);
  const start = useRef<{ x: number; y: number; t: number; width: number } | null>(null);
  const axis = useRef<"undecided" | "horizontal" | "vertical">("undecided");

  function onPointerDown(event: ReactPointerEvent<HTMLElement>): void {
    if (!event.isPrimary) return;
    start.current = {
      x: event.clientX,
      y: event.clientY,
      t: event.timeStamp,
      width: event.currentTarget.offsetWidth || 1,
    };
    axis.current = "undecided";
  }

  function onPointerMove(event: ReactPointerEvent<HTMLElement>): void {
    const origin = start.current;
    if (!origin) return;
    const dx = event.clientX - origin.x;
    const dy = event.clientY - origin.y;

    if (axis.current === "undecided") {
      if (Math.abs(dx) < SLOP_PX && Math.abs(dy) < SLOP_PX) return;
      // Whichever axis moved more decides; a vertical decision releases the
      // gesture entirely so the list scrolls as it should.
      axis.current = Math.abs(dx) > Math.abs(dy) ? "horizontal" : "vertical";
      if (axis.current === "horizontal") event.currentTarget.setPointerCapture(event.pointerId);
    }
    if (axis.current !== "horizontal") return;

    const armed = Math.abs(dx) > origin.width * COMMIT_FRACTION;
    setState({ offset: dx, armed, direction: dx < 0 ? "left" : "right", dragging: true });
  }

  function onPointerUp(event: ReactPointerEvent<HTMLElement>): void {
    const origin = start.current;
    start.current = null;
    if (!origin || axis.current !== "horizontal") {
      setState(IDLE);
      return;
    }
    const dx = event.clientX - origin.x;
    const elapsed = Math.max(1, event.timeStamp - origin.t);
    const velocity = Math.abs(dx) / elapsed;
    const committed = Math.abs(dx) > origin.width * COMMIT_FRACTION || velocity > FLICK_VELOCITY;

    setState(IDLE);
    if (committed && dx !== 0) onCommit(dx < 0 ? "left" : "right");
  }

  return {
    state,
    handlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp,
      onPointerCancel: () => {
        start.current = null;
        setState(IDLE);
      },
    },
  };
}
